import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Users, UserPlus, Crown, Lock, Eye, Hourglass, MessageCircle, BadgeCheck, Search, Loader2 } from "lucide-react";
import FriendButton from "@/components/app/FriendButton";

export default function Friends() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState({ count: 0, items: [], vip_required: true });
  const [outgoing, setOutgoing] = useState([]);
  const [tab, setTab] = useState("friends");
  const [views, setViews] = useState({ count: 0, vip_required: true, viewers: [] });

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const load = async () => {
    const [fr, inc, out, v] = await Promise.all([
      api.get("/friends").then((r) => r.data),
      api.get("/friends/requests/incoming").then((r) => r.data),
      api.get("/friends/requests/outgoing").then((r) => r.data),
      api.get("/profile/views").then((r) => r.data),
    ]);
    setFriends(fr); setIncoming(inc); setOutgoing(out); setViews(v);
  };
  const handleSearch = async (overrideQuery) => {
    const q = typeof overrideQuery === "string" ? overrideQuery : searchQuery;
    setSearching(true);
    try {
      const { data } = await api.get(`/users/search?q=${encodeURIComponent(q.trim())}`);
      setSearchResults(data);
      setHasSearched(true);
    } catch (err) {
      console.warn("User search error:", err);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    load();
    handleSearch("");
  }, []);

  const startDM = async (uid) => {
    try {
      const { data } = await api.post(`/chats/direct/${uid}`);
      nav(`/chats/${data.id}`);
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <button onClick={() => nav(-1)} className="btn-nb mb-5" data-testid="back-btn"><ArrowLeft className="w-4 h-4" strokeWidth={2.5}/> {t("common.back")}</button>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-1">{t("friends.title")} <span className="bg-gradient-to-r from-[#0F9D8F] via-[#14C8B0] to-[#5EEAD4] bg-clip-text text-transparent">{t("friends.highlight")}</span></h1>
      <p className="text-[#4A4F58] mb-5">{friends.length} {t("friends.countFriends")} · {incoming.count} {t("friends.countPending")}</p>

      {/* User Search Bar & Button */}
      <form onSubmit={(e) => { e.preventDefault(); handleSearch(searchQuery); }} className="mb-5 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#7C828D]" strokeWidth={2.5} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              const val = e.target.value;
              setSearchQuery(val);
              handleSearch(val);
            }}
            placeholder="Search users by name or @username..."
            className="w-full bg-white border-2 border-[#121212] rounded-2xl pl-11 pr-14 py-3 text-sm font-semibold placeholder-[#7C828D] outline-none shadow-nb-sm focus:border-[#0F9D8F] transition-all"
            data-testid="user-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => { setSearchQuery(""); handleSearch(""); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#7C828D] hover:text-black bg-[#F4F1E8] px-2 py-1 rounded-lg"
            >
              Clear
            </button>
          )}
        </div>
        <button
          type="submit"
          disabled={searching}
          className="bg-[#00CED1] hover:bg-[#14C8B0] text-black font-black px-5 py-3 rounded-2xl border-2 border-[#121212] shadow-nb-sm disabled:opacity-50 flex items-center gap-2 shrink-0 text-sm"
          data-testid="user-search-btn"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" strokeWidth={3} />}
          Search
        </button>
      </form>

      {/* Search Results Drawer / Card List */}
      {hasSearched && (
        <div className="mb-6 space-y-3 p-4 bg-[#F4F1E8] rounded-3xl border-2 border-[#121212] shadow-nb-sm" data-testid="search-results-section">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-black text-lg text-[#0A0B0D]">
              Search Results ({searchResults.length})
            </h2>
            <button
              onClick={() => { setSearchResults([]); setHasSearched(false); setSearchQuery(""); }}
              className="text-xs font-bold text-[#7C828D] hover:text-black"
            >
              Close Results ✕
            </button>
          </div>
          {searchResults.length === 0 ? (
            <div className="p-6 text-center text-sm font-bold text-[#7C828D]">
              No users found matching &quot;{searchQuery}&quot;. Try searching by name or @username.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {searchResults.map((u) => (
                <div key={u.id} className="card-nb p-3.5 flex items-center justify-between gap-3 bg-white" data-testid={`search-user-${u.id}`}>
                  <Link to={`/u/${u.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-11 h-11 rounded-full bg-[#E0F7F6] border border-[#121212] shadow-nb-sm flex items-center justify-center font-display font-bold text-base overflow-hidden shrink-0">
                      {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-sm flex items-center gap-1 truncate">
                        {u.name} {u.verified && <BadgeCheck className="w-4 h-4 text-[#00B4D8]" strokeWidth={2.5} />}
                      </div>
                      {u.username && (
                        <div className="text-xs font-bold text-[#0F9D8F] tracking-tight">@{u.username}</div>
                      )}
                      <div className="text-[11px] text-[#7C828D] truncate">{u.location || u.bio || "TANTA member"}</div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Link to={`/u/${u.id}`} className="btn-nb !py-1.5 !px-2.5 text-xs font-bold" data-testid={`view-profile-${u.id}`}>
                      View Profile
                    </Link>
                    <FriendButton userId={u.id} status={typeof u.friend_status === "string" ? u.friend_status : u.friend_status?.status} compact onChange={() => handleSearch()} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mb-5 p-1 bg-[#F4F2ED] rounded-full border border-[#E8E5DD] w-fit">
        {[
          { id: "friends", label: `${t("friends.tabs.friends")} (${friends.length})` },
          { id: "incoming", label: `${t("friends.tabs.incoming")} (${incoming.count})` },
          { id: "outgoing", label: `${t("friends.tabs.outgoing")} (${outgoing.length})` },
          { id: "views", label: `${t("friends.tabs.views")} (${views.count})` },
        ].map((tabItem) => (
          <button key={tabItem.id} onClick={() => setTab(tabItem.id)} data-testid={`tab-${tabItem.id}`}
            className={`px-4 py-1.5 rounded-full font-display font-semibold text-xs tracking-tight transition-all ${
              tab === tabItem.id ? "bg-[#0F1115] text-white shadow-soft" : "text-[#4A4F58]"
            }`}>{tabItem.label}</button>
        ))}
      </div>

      {tab === "friends" && (
        friends.length === 0 ? (
          <Empty title={t("friends.emptyTitle")} body={t("friends.emptyBody")} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {friends.map((u) => (
              <div key={u.id} className="card-nb p-4 flex items-center gap-3 bg-white" data-testid={`friend-${u.id}`}>
                <Link to={`/u/${u.id}`} className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-[#FFF1D6] ring-1 ring-black/5 flex items-center justify-center font-display font-bold overflow-hidden">
                    {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover"/> : u.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold flex items-center gap-1 truncate">{u.name} {u.verified && <BadgeCheck className="w-4 h-4 text-[#14C8B0]" strokeWidth={2.5}/>}</div>
                    <div className="text-[11px] text-[#8A8F98] truncate">{u.location || u.bio || "—"}</div>
                  </div>
                </Link>
                <button onClick={() => startDM(u.id)} className="btn-nb !py-2 !px-3" data-testid={`dm-${u.id}`}>
                  <MessageCircle className="w-4 h-4" strokeWidth={2.5}/>
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "incoming" && (
        incoming.vip_required ? (
          <VipLockCard
            title={t(incoming.count === 1 ? "friends.lockTitlePending" : "friends.lockTitlePendingPlural", { count: incoming.count })}
            body={t("friends.lockBodyPending")}
          />
        ) : incoming.items.length === 0 ? (
          <Empty title={t("friends.noRequestsTitle")} body={t("friends.noRequestsBody")} />
        ) : (
          <div className="space-y-3">
            {incoming.items.map((r) => (
              <div key={r.id} className="card-nb p-4 flex items-center gap-3 bg-[#FFF1D6]" data-testid={`incoming-${r.id}`}>
                <Link to={`/u/${r.from_id}`} className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-white ring-1 ring-black/5 flex items-center justify-center font-display font-bold overflow-hidden">
                    {r.from_user?.avatar_url ? <img src={r.from_user.avatar_url} alt="" className="w-full h-full object-cover"/> : r.from_user?.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate">{r.from_user?.name}</div>
                    <div className="text-[11px] text-[#8A8F98]">{t("friends.wantsToBeFriends")}</div>
                  </div>
                </Link>
                <FriendButton userId={r.from_id} status="incoming_pending" requestId={r.id} compact onChange={load}/>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "outgoing" && (
        outgoing.length === 0 ? (
          <Empty title={t("friends.noPendingTitle")} body={t("friends.noPendingBody")} />
        ) : (
          <div className="space-y-3">
            {outgoing.map((r) => (
              <div key={r.id} className="card-nb p-4 flex items-center gap-3 bg-white" data-testid={`outgoing-${r.id}`}>
                <Link to={`/u/${r.to_id}`} className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-[#EBE4FF] ring-1 ring-black/5 flex items-center justify-center font-display font-bold overflow-hidden">
                    {r.to_user?.avatar_url ? <img src={r.to_user.avatar_url} alt="" className="w-full h-full object-cover"/> : r.to_user?.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate">{r.to_user?.name}</div>
                    <div className="text-[11px] text-[#8A8F98] flex items-center gap-1"><Hourglass className="w-3 h-3"/> {t("friends.awaiting")}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "views" && (
        views.vip_required ? (
          <VipLockCard
            title={t("friends.lockTitleViews", { count: views.count })}
            body={t("friends.lockBodyViews")}
          />
        ) : views.viewers.length === 0 ? (
          <Empty title={t("friends.noViewsTitle")} body={t("friends.noViewsBody")} />
        ) : (
          <div className="space-y-2">
            {views.viewers.map((v) => (
              <Link to={`/u/${v.user.id}`} key={`${v.user.id}-${v.viewed_at}`} data-testid={`view-${v.user.id}`} className="card-nb p-3 flex items-center gap-3 bg-white">
                <Eye className="w-4 h-4 text-[#0F9D8F]" strokeWidth={2.5}/>
                <div className="w-9 h-9 rounded-full bg-[#DEF1F5] ring-1 ring-black/5 flex items-center justify-center font-display font-bold overflow-hidden">
                  {v.user.avatar_url ? <img src={v.user.avatar_url} alt="" className="w-full h-full object-cover"/> : v.user.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold truncate">{v.user.name}</div>
                  <div className="text-[11px] text-[#8A8F98]">{t("friends.viewed", { when: new Date(v.viewed_at).toLocaleString() })}</div>
                </div>
              </Link>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function Empty({ title, body }) {
  return (
    <div className="card-nb p-10 text-center bg-[#F4F2ED]" data-testid="empty">
      <Users className="w-12 h-12 mx-auto mb-3" strokeWidth={2.25}/>
      <h3 className="font-display font-extrabold text-xl mb-1 tracking-tight">{title}</h3>
      <p className="text-[#4A4F58] text-sm">{body}</p>
    </div>
  );
}

function VipLockCard({ title, body }) {
  const { t } = useTranslation();
  return (
    <div className="card-nb p-7 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1F2228 0%, #0F1115 50%, #F9C349 220%)" }} data-testid="vip-lock">
      <div className="absolute -right-6 -bottom-6 w-40 h-40 rounded-full bg-[#F9C349]/30 blur-3xl"/>
      <Crown className="w-10 h-10 text-[#F9C349] mb-3" strokeWidth={2.5} fill="#F9C349"/>
      <h3 className="font-display font-extrabold text-2xl mb-1 tracking-tight">{title}</h3>
      <p className="text-white/80 text-sm mb-4">{body}</p>
      <Link to="/vip" className="btn-nb btn-nb-warn" data-testid="vip-upgrade-link"><Lock className="w-4 h-4" strokeWidth={2.5}/> {t("friends.unlockWithVip")}</Link>
    </div>
  );
}
